import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";

import { getConfig } from "lib/get-config";
import { captureException } from "lib/logger";
import { fetchBookPath } from "lib/urls/book-path";

import {
  deleteQuote,
  getQuotes,
  postQuote,
  publishQuote,
  updateQuote,
  type CreateQuoteInput,
  type UpdateQuoteInput,
} from "./get-quotes";
import {
  addLocalQuote,
  deleteLocalQuote,
  getLocalQuotes,
  updateLocalQuote,
} from "./local";

type QuoteResult = UpdateQuoteInput;

export function useQuotes() {
  const { art, user } = getConfig();
  const queryClient = useQueryClient();
  const queryKey = user ? ["quotes", art] : ["local-quotes", art];

  const { data: quotes } = useSuspenseQuery({
    queryKey,
    queryFn: () =>
      user ? getQuotes(art) : (getLocalQuotes(art) as QuoteResult[]),
  });

  async function createQuote(
    params: CreateQuoteInput
  ): Promise<[QuoteResult | null, Error | null]> {
    const newQuote: UpdateQuoteInput = {
      id: params.id ?? uuid(),
      created_at: new Date().toISOString(),
      title: "",
      ...params,
      is_epub: false,
    };

    const previous = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, [...(quotes || []), newQuote]);

    try {
      if (user) {
        await postQuote(art, newQuote);
      } else {
        addLocalQuote(newQuote, art);
      }
      return [newQuote, null];
    } catch (error) {
      queryClient.setQueryData(queryKey, previous);
      captureException(error as Error);
      return [null, error as Error];
    }
  }

  async function handleDeleteQuote(
    quoteId: string
  ): Promise<[string | null, Error | null]> {
    const previous = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(
      queryKey,
      (quotes || []).filter(q => q.id !== quoteId)
    );

    try {
      if (user) {
        await deleteQuote(art, quoteId);
      } else {
        deleteLocalQuote(quoteId);
      }
      return [quoteId, null];
    } catch (error) {
      queryClient.setQueryData(queryKey, previous);
      captureException(error as Error);
      return [null, error as Error];
    }
  }

  async function handleUpdateQuote(
    quoteId: string,
    updates: Partial<Pick<UpdateQuoteInput, "color" | "note_text">>
  ): Promise<[UpdateQuoteInput | null, Error | null]> {
    const quote = quotes?.find(q => q.id === quoteId);
    if (!quote) {
      return [null, new Error("Quote not found")];
    }

    const updatedQuote = { ...quote, ...updates };

    const previous = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(
      queryKey,
      (quotes || []).map(q => (q.id === quoteId ? updatedQuote : q))
    );

    try {
      if (user) {
        // @todo: разобраться с типами цветов на бэке
        await updateQuote(art, updatedQuote as UpdateQuoteInput);
      } else {
        updateLocalQuote(quoteId, updates);
      }
      // @todo: разобраться с типами цветов на бэке
      return [updatedQuote as UpdateQuoteInput, null];
    } catch (error) {
      queryClient.setQueryData(queryKey, previous);
      captureException(error as Error);
      return [null, error as Error];
    }
  }

  async function handlePublishQuote(
    quoteId: string
  ): Promise<[boolean, Error | null]> {
    if (!user) {
      return [false, new Error("Publishing requires authentication")];
    }

    try {
      const res = await publishQuote(quoteId);
      if (res.response?.status === 204) {
        return [true, null];
      }
      return [false, new Error("Failed to publish quote")];
    } catch (error) {
      captureException(error as Error);
      return [false, error as Error];
    }
  }

  async function shareToVk(
    quoteId: string,
    quoteText: string
  ): Promise<Error | null> {
    if (!user) {
      return new Error("Sharing requires authentication");
    }

    try {
      const res = await publishQuote(quoteId);
      if (res.response?.status !== 204) {
        return new Error("Failed to publish quote");
      }

      const bookPath = await fetchBookPath(art);
      const viewQuoteUrl = `${import.meta.env.VITE_LITRES_URL}${bookPath}/citata/${quoteId}/`;
      const text = `\u00AB${quoteText.trim()}\u00BB`;
      const vkShareUrl = `https://vk.com/share.php?url=${encodeURIComponent(viewQuoteUrl)}&comment=${encodeURIComponent(text)}`;

      window.open(vkShareUrl, "vk_share", "width=550,height=450");
      return null;
    } catch (error) {
      return error as Error;
    }
  }

  return {
    quotes,
    createQuote,
    deleteQuote: handleDeleteQuote,
    updateQuote: handleUpdateQuote,
    publishQuote: handlePublishQuote,
    shareToVk,
  };
}
